import { ProBadge } from './ProBadge';
import { effectiveName } from '../utils/userDisplay';

/**
 * Single source of truth for the seller-identity block.
 *
 * PARTICULIER:
 *   → display_name || name (single line)
 *   → no PRO badge
 *
 * PROFESSIONNEL (seller_type === "business"):
 *   Line 1 (primary, bold): company_name (fallback: display_name || name)
 *   Line 2 (smaller, muted): display_name || name
 *   PRO badge inline next to the primary line
 *
 * Accepts either a flat user-shaped object OR a listing-shaped object —
 * both expose the same fields (`seller_type`, `business_name`,
 * `display_name`, `name`/`user_name`). When called with a listing, the
 * `name` field is read from `user_name` (the denormalized projection).
 *
 * `variant`:
 *   • "stack"  (default) — 2-line primary/secondary block.
 *   • "inline" — single line "Company · Name" (used in the messages chat
 *                header where vertical real estate is tight).
 */
export const SellerName = ({
  user,
  variant = 'stack',
  badgeSize = 'inline',
  className = '',
  primaryClassName = 'font-semibold text-text_strong',
  secondaryClassName = 'text-xs text-text_muted',
  testIdPrefix = 'seller',
}) => {
  if (!user) return null;

  // Normalize the input — listings expose `user_name` instead of `name`.
  const fallbackName = user.user_name || user.name || '';
  const personal = (user.display_name || '').trim() || fallbackName;
  const isPro = user.seller_type === 'business';
  const company = isPro ? (user.business_name || '').trim() : '';

  if (!isPro) {
    // Particulier — single-line, no badge.
    return (
      <span className={`inline-flex items-center ${className}`}>
        <span className={primaryClassName} data-testid={`${testIdPrefix}-name`}>
          {personal}
        </span>
      </span>
    );
  }

  // Professionnel — company over name + PRO badge. If the business
  // didn't fill in `business_name`, fall back to the personal name as
  // the primary line so we never render an empty heading.
  const primary = company || personal;
  const secondary = company ? personal : null;

  if (variant === 'inline') {
    return (
      <span className={`inline-flex items-center gap-2 ${className}`}>
        <span className={primaryClassName} data-testid={`${testIdPrefix}-name`}>
          {primary}
        </span>
        <ProBadge size={badgeSize} />
        {secondary && (
          <span className="text-xs text-text_muted" data-testid={`${testIdPrefix}-personal-name`}>
            · {secondary}
          </span>
        )}
      </span>
    );
  }

  return (
    <div className={`flex flex-col min-w-0 ${className}`}>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`${primaryClassName} truncate`} data-testid={`${testIdPrefix}-name`}>
          {primary}
        </span>
        <ProBadge size={badgeSize} className="flex-shrink-0" />
      </div>
      {secondary && (
        <span className={`${secondaryClassName} truncate`} data-testid={`${testIdPrefix}-personal-name`}>
          {secondary}
        </span>
      )}
    </div>
  );
};

// Re-export for callers that just want the resolved string (no JSX).
export const resolveSellerPrimary = (user) => {
  if (!user) return '';
  const personal = effectiveName(user) || user.user_name || '';
  if (user.seller_type === 'business') {
    return (user.business_name || '').trim() || personal;
  }
  return personal;
};
